import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, videoWatchUrl, courseInstructors } from '@astro/api'
import { appUrl } from '@astro/config'
import { useCourseDetails, useDeleteLiveClass, useLiveClasses } from '../useDashboard'
import {
  SectionHead,
  StatTile,
  ClassRow,
  ClassSkeletonCard,
  ConfirmDeleteClassModal,
  Badge,
  MetaChip,
  InstructorsRow,
  isRecordedRow,
} from './academyShared'
import CourseInstallmentPlan from './CourseInstallmentPlan'
import CreateCourseModal from './CreateCourseModal'
import CreateLessonModal from './CreateLessonModal'
import CreateLiveClassModal from './CreateLiveClassModal'
import CreateTopicModal from './CreateTopicModal'
import RescheduleLiveClassModal from './RescheduleLiveClassModal'

/** Education app's public page for a course — slug preferred, id works too. */
const courseSiteHref = (ref) => appUrl('education', `/classes/${ref}`)

/** Read-payload HTML blurb → plain text. */
function stripHtml(html) {
  return String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

const goldBtnCls =
  'bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed'
const outlineBtnCls =
  'border border-stone-200 text-stone-700 font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:bg-stone-50 transition-all'

/** One lesson line inside a curriculum module — number, type icon, title,
 * preview chip, and a Watch link when the lesson carries a video. */
function LessonRow({ lesson, index }) {
  const video = videoWatchUrl(lesson.videoUrl)
  const isText = String(lesson.type || '').toLowerCase() === 'text'
  const preview = Boolean(lesson.isPreview ?? lesson.is_preview)
  return (
    <div className="flex items-center gap-3 py-2.5 px-2">
      <span className="num text-[10px] font-bold text-stone-300 w-6 text-right shrink-0">{index}</span>
      <span className="w-8 h-8 rounded-lg bg-stone-50 border border-stone-100 flex items-center justify-center text-sm shrink-0">
        {isText ? '📝' : String(lesson.type || '').toLowerCase() === 'file' ? '📎' : '🎬'}
      </span>
      <p className="text-sm font-semibold text-astroDark truncate flex-1 min-w-0">{lesson.title || 'Lesson'}</p>
      {preview && (
        <span className="hidden sm:inline text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-green-50 text-green-600 shrink-0">
          Free preview
        </span>
      )}
      {video ? (
        <a
          href={video}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline shrink-0"
        >
          Watch →
        </a>
      ) : (
        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-300 shrink-0">{isText ? 'Reading' : '—'}</span>
      )}
    </div>
  )
}

/** One module block — header with running number, lesson count and the
 * module's own actions (add lesson / edit / delete), then every lesson
 * listed out in order (nothing collapsed on this page). */
function ModuleBlock({ module, index, onAddLesson, onEdit, onDelete }) {
  const lessons = Array.isArray(module.lessons) ? module.lessons : []
  return (
    <div className="rounded-2xl border border-stone-100 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-stone-50/60 border-b border-stone-100">
        <span className="w-8 h-8 rounded-lg bg-[#3D0A0A] text-[#D4A017] text-[11px] font-bold flex items-center justify-center shrink-0">
          {String(index).padStart(2, '0')}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-astroDark truncate">{module.title || `Module ${index}`}</p>
          <p className="text-[10px] text-stone-400 uppercase tracking-wide font-bold mt-0.5">
            {lessons.length} lesson{lessons.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => onAddLesson(module)}
            className="text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline px-1"
          >
            ＋ Lesson
          </button>
          <button
            type="button"
            onClick={() => onEdit(module)}
            title="Edit module"
            aria-label={`Edit module ${module.title || index}`}
            className="w-7 h-7 rounded-lg border border-stone-200 text-stone-400 hover:text-astroDark hover:bg-white transition-colors text-xs flex items-center justify-center"
          >
            ✎
          </button>
          <button
            type="button"
            onClick={() => onDelete(module)}
            title="Delete module"
            aria-label={`Delete module ${module.title || index}`}
            className="w-7 h-7 rounded-lg border border-stone-100 text-stone-300 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors text-xs flex items-center justify-center"
          >
            🗑
          </button>
        </div>
      </div>
      {lessons.length ? (
        <div className="px-2 py-1 divide-y divide-stone-50">
          {lessons.map((l, i) => <LessonRow key={l.id ?? i} lesson={l} index={i + 1} />)}
        </div>
      ) : (
        <p className="px-4 py-3 text-xs text-stone-400">
          No lessons in this module yet —{' '}
          <button type="button" onClick={() => onAddLesson(module)} className="font-bold text-goldAccent hover:underline">
            add the first one
          </button>
          .
        </p>
      )}
    </div>
  )
}

/** Recording row for embedded `previousRecordings` entries that the
 * live-class API didn't cover (no richer data available for these). */
function EmbeddedRecordingRow({ rec }) {
  return (
    <div className="flex items-center gap-3 py-2.5 px-2">
      <div className="w-11 h-11 rounded-lg bg-stone-400 text-white flex items-center justify-center shrink-0">
        <span className="text-base">🎥</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-astroDark truncate">{rec.title || 'Recording'}</p>
        <p className="text-[10px] text-stone-400 uppercase tracking-wide font-bold mt-0.5 truncate">
          {rec.date ? `🗓 ${rec.date}` : ''}{rec.duration ? ` · ⏳ ${rec.duration}` : ''}
        </p>
      </div>
      {rec.recordingUrl ? (
        <a href={rec.recordingUrl} target="_blank" rel="noreferrer" className="text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline shrink-0">
          Watch →
        </a>
      ) : null}
    </div>
  )
}

/** Delete confirmation dialog — same portal/backdrop idiom as the create
 * modals, red accent. Shared by the course and module delete flows. */
function ConfirmDeleteModal({ heading, message, keepLabel, confirmLabel, busy, error, onConfirm, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose, busy])

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={busy ? undefined : onClose} aria-hidden="true" />
      <div role="alertdialog" aria-modal="true" aria-label={heading} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-500 text-xl flex items-center justify-center mb-4">🗑</div>
        <h3 className="text-xl text-astroDark font-royal">{heading}</h3>
        <p className="text-sm text-stone-500 mt-2">{message}</p>
        {error && (
          <div className="mt-4 px-4 py-3 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-100">
            {error}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-stone-100">
          <button type="button" onClick={onClose} disabled={busy} className={outlineBtnCls}>
            {keepLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="bg-red-600 text-white font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:bg-red-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}

/* ─────────────────────────── Page ─────────────────────────── */
export default function InstructorCourseDetails({ courseId, seed = null, onBack }) {
  const queryClient = useQueryClient()
  const { course, isLoading, isError, refetch } = useCourseDetails(courseId)
  const { classes } = useLiveClasses()

  const [showEdit, setShowEdit] = useState(false)
  // Add Lesson: `open` gates it; `topic` pre-selects+locks the module when
  // launched from a module block (null → picked inside the form).
  const [lessonModal, setLessonModal] = useState({ open: false, topic: null })
  // Add/Edit Module: `topic` null = create, otherwise the module being edited.
  const [topicModal, setTopicModal] = useState({ open: false, topic: null })
  // Module delete: the curriculum module awaiting confirmation (null = closed).
  const [topicToDelete, setTopicToDelete] = useState(null)
  const [topicDeleteError, setTopicDeleteError] = useState(null)
  const [showClass, setShowClass] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const openLessonModal = (topic = null) => setLessonModal({ open: true, topic })
  const openTopicModal = (topic = null) => setTopicModal({ open: true, topic })
  const askDeleteTopic = (topic) => { setTopicDeleteError(null); setTopicToDelete(topic) }
  // Live-class delete: the normalised row awaiting confirmation (null = closed).
  const [classToDelete, setClassToDelete] = useState(null)
  const [classDeleteError, setClassDeleteError] = useState(null)
  const deleteClassMutation = useDeleteLiveClass()
  const askDeleteClass = (c) => { setClassDeleteError(null); setClassToDelete(c) }
  // Live-class reschedule: the normalised row being moved (null = closed).
  const [classToReschedule, setClassToReschedule] = useState(null)
  const confirmDeleteClass = () =>
    deleteClassMutation.mutate(classToDelete.id, {
      onSuccess: () => setClassToDelete(null),
      onError: (err) => setClassDeleteError(err?.message || 'Could not delete the class. Please try again.'),
    })

  const topicDeleteMutation = useMutation({
    mutationFn: (topicId) => api.courses.removeTopic(topicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'course-detail', String(courseId)] })
      setTopicToDelete(null)
    },
    onError: (err) => setTopicDeleteError(err?.message || 'Could not delete the module. Please try again.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.courses.remove(courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'instructor-courses'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'courses'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'live-classes'] })
      queryClient.removeQueries({ queryKey: ['dashboard', 'course-detail', String(courseId)] })
      onBack()
    },
    onError: (err) => setDeleteError(err?.message || 'Could not delete the course. Please try again.'),
  })

  // Header paints from the list-card seed until the detail read lands.
  const title = course?.title ?? seed?.title ?? 'Course'
  const img = course?.coverImage ?? seed?.img ?? null

  // Curriculum — modules with their lessons, from the detail read only.
  const modules = Array.isArray(course?.curriculum) ? course.curriculum : []
  const lessonCount = modules.reduce((n, m) => n + (m.lessons?.length || 0), 0)

  // This course's sessions from the instructor's live-class rows (richer than
  // the embedded detail rows: host links, recording state, status pills).
  const classRows = classes.filter((c) => String(c.courseId) === String(courseId))
  const liveRows = classRows
    .filter((c) => !isRecordedRow(c))
    .sort((a, b) => Number(b.isLive) - Number(a.isLive))
  const recordedRows = classRows.filter(isRecordedRow)
  // Embedded recordings the live-class API didn't cover (e.g. older sessions).
  const extraRecordings = (Array.isArray(course?.previousRecordings) ? course.previousRecordings : [])
    .filter((r) => !classRows.some((c) => String(c.id) === String(r.id)))
  const liveNow = classRows.filter((c) => c.isLive).length

  // Pricing — same discount rule as the browse cards.
  const price = Number(course?.price)
  const discount = Number(course?.discountPrice)
  const hasDiscount = Number.isFinite(discount) && Number.isFinite(price) && discount > 0 && discount < price
  const finalPrice = hasDiscount ? discount : price
  const priceLabel = !Number.isFinite(finalPrice) || finalPrice === 0 ? 'Free' : `₹${finalPrice.toLocaleString('en-IN')}`

  const students = course?.studentsEnrolled ?? seed?.students ?? 0
  const instructors = course ? courseInstructors(course) : []
  const isLive = String(course?.courseType ?? '').toLowerCase() === 'live'
  const blurb = stripHtml(course?.shortDescription)
  const siteRef = course?.slug ?? seed?.courseRef ?? courseId

  const stats = [
    { key: 'modules', icon: '🗂', label: 'Modules', value: modules.length },
    { key: 'lessons', icon: '📖', label: 'Lessons', value: lessonCount },
    { key: 'classes', icon: '📼', label: 'Live Classes', value: classRows.length },
    { key: 'recordings', icon: '🎥', label: 'Recordings', value: recordedRows.length + extraRecordings.length },
    { key: 'students', icon: '🎓', label: 'Students', value: students },
    { key: 'reviews', icon: '⭐', label: 'Reviews', value: course?.totalReviews ?? seed?.reviews ?? 0 },
  ]

  return (
    <div className="space-y-6">
      {/* ── Back to the course list ── */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-stone-500 hover:text-astroDark transition-colors"
      >
        ← Back to My Courses
      </button>

      {isError && !course ? (
        <div className="dash-card p-4 sm:p-6">
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🛰️</div>
            <p className="text-base font-semibold text-astroDark mb-1">Couldn&rsquo;t load this course</p>
            <p className="text-sm text-stone-400 mb-4">Something went wrong while fetching the course details. Please try again.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ══ Hero — identity, badges, meta, stats and every course action ══ */}
          <div className="dash-card p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <span className="eyebrow text-amber-700">Vedic Academy · Course</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setShowEdit(true)}
                  disabled={!course}
                  title={course ? undefined : 'Available once the course details load'}
                  className={goldBtnCls}
                >
                  ✎ Edit Course
                </button>
                {/* <button
                  type="button"
                  onClick={() => { setDeleteError(null); setConfirmDelete(true) }}
                  className="border border-red-200 text-red-600 font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:bg-red-50 transition-all"
                >
                  🗑 Delete
                </button> */}
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-5">
              {img ? (
                <img src={img} alt="" className="w-full md:w-56 h-36 rounded-2xl object-cover shrink-0" />
              ) : (
                <div className="w-full md:w-56 h-36 rounded-2xl bg-[#F5E6D3] text-[#A67C00] text-4xl flex items-center justify-center shrink-0">
                  📚
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  {(course?.category ?? seed?.category) && <Badge>{course?.category ?? seed?.category}</Badge>}
                  {(course?.level ?? seed?.level) && (
                    <Badge className="bg-stone-100 text-stone-500 capitalize">{course?.level ?? seed?.level}</Badge>
                  )}
                  {course && (
                    <Badge className={isLive ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-600'}>
                      {isLive ? '🔴 Live Course' : '🎬 Recorded'}
                    </Badge>
                  )}
                  {liveNow > 0 && (
                    <Badge className="bg-red-50 text-red-500">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse mr-1" />
                      {liveNow} Live Now
                    </Badge>
                  )}
                </div>
                <h2 className="text-2xl text-astroDark font-royal mt-2 leading-tight">{title}</h2>
                {isLoading && !course ? (
                  <div className="space-y-2 mt-3 animate-pulse" aria-hidden="true">
                    <div className="h-2.5 w-3/4 bg-stone-100 rounded-full" />
                    <div className="h-2.5 w-1/2 bg-stone-100 rounded-full" />
                  </div>
                ) : blurb ? (
                  <p className="text-sm text-stone-500 mt-2">{blurb}</p>
                ) : null}

                <InstructorsRow instructors={instructors} showDesignation />

                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  <MetaChip icon="💰">
                    {priceLabel}
                    {hasDiscount && <s className="text-stone-300 ml-1">₹{price.toLocaleString('en-IN')}</s>}
                  </MetaChip>
                  {course?.language && <MetaChip icon="🗣">{course.language}</MetaChip>}
                  {course?.lastUpdated && <MetaChip icon="🗓">Updated {course.lastUpdated}</MetaChip>}
                  {course?.certificateAvailable && <MetaChip icon="📜">Certificate</MetaChip>}
                  {course?.lifetimeAccess && <MetaChip icon="♾️">Lifetime access</MetaChip>}
                  {course?.visibility && <MetaChip icon={course.visibility === 'public' ? '🌐' : '🔒'}>{course.visibility}</MetaChip>}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-stone-100">
              {stats.map((s) => (
                <StatTile key={s.key} icon={s.icon} label={s.label} value={isLoading && !course ? '—' : s.value} />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-5 pt-5 border-t border-stone-100">
              <button type="button" onClick={() => openTopicModal()} className={outlineBtnCls}>
                ＋ Add Module
              </button>
              <button type="button" onClick={() => openLessonModal()} className={outlineBtnCls}>
                ＋ Add Lesson
              </button>
              <button type="button" onClick={() => setShowClass(true)} className={outlineBtnCls}>
                ＋ Schedule Live Class
              </button>
              <a
                href={courseSiteHref(siteRef)}
                target="_blank"
                rel="noreferrer"
                className="ml-auto text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline"
              >
                View on site ↗
              </a>
            </div>
          </div>

          {/* ══ Pricing — the part-payment schedule and what it has collected ══ */}
          {course && (
            <CourseInstallmentPlan
              courseId={course.id}
              price={course.price}
              acceptPartPayment={Boolean(course.acceptPartPayment)}
              minPartPrice={course.minPartPrice}
            />
          )}

          {/* ══ Curriculum — module-first: modules hold the lessons ══ */}
          <section className="dash-card p-4 sm:p-6">
            <SectionHead eyebrow="📖 Curriculum" eyebrowClass="text-amber-700" title="Modules & Lessons" count={isLoading && !course ? null : lessonCount}>
              {modules.length > 0 && (
                <button type="button" onClick={() => openTopicModal()} className={outlineBtnCls}>
                  ＋ Add Module
                </button>
              )}
            </SectionHead>
            {isLoading && !course ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Array.from({ length: 2 }, (_, i) => <ClassSkeletonCard key={i} />)}
              </div>
            ) : modules.length === 0 ? (
              <div className="py-8 text-center">
                <div className="text-4xl mb-3 opacity-40">🗂</div>
                <p className="text-base font-semibold text-astroDark mb-1">No modules yet</p>
                <p className="text-sm text-stone-400 mb-4">
                  Modules organise your curriculum — create one first, then add lessons inside it.
                </p>
                <button type="button" onClick={() => openTopicModal()} className={goldBtnCls}>
                  Create your first module
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {modules.map((m, i) => (
                  <ModuleBlock
                    key={m.id ?? i}
                    module={m}
                    index={i + 1}
                    onAddLesson={openLessonModal}
                    onEdit={openTopicModal}
                    onDelete={askDeleteTopic}
                  />
                ))}
              </div>
            )}
          </section>

          {/* ══ Live classes — every session scheduled for this course ══ */}
          <section className="dash-card p-4 sm:p-6">
            <SectionHead eyebrow="🔴 Live Classes" eyebrowClass="text-red-500" title="Sessions for this Course" count={liveRows.length} />
            {liveRows.length === 0 ? (
              <p className="text-sm text-stone-400 py-2">
                No live sessions scheduled for this course yet — schedule one from the button above.
              </p>
            ) : (
              <div className="divide-y divide-stone-50 -mx-2">
                {liveRows.map((c) => (
                  <ClassRow key={c.key} c={c} onDelete={askDeleteClass} onReschedule={setClassToReschedule} />
                ))}
              </div>
            )}
          </section>

          {/* ══ Recordings — finished recorded sessions of this course ══ */}
          <section className="dash-card p-4 sm:p-6">
            <SectionHead eyebrow="🎥 Recordings" eyebrowClass="text-amber-700" title="Recordings Library" count={recordedRows.length + extraRecordings.length} />
            {recordedRows.length === 0 && extraRecordings.length === 0 ? (
              <p className="text-sm text-stone-400 py-2">
                No recordings yet — turn on <span className="font-semibold text-stone-500">&ldquo;Record this class&rdquo;</span> when
                scheduling a session and it appears here after it ends.
              </p>
            ) : (
              <div className="divide-y divide-stone-50 -mx-2">
                {recordedRows.map((c) => <ClassRow key={c.key} c={c} onDelete={askDeleteClass} />)}
                {extraRecordings.map((r, i) => <EmbeddedRecordingRow key={`rec-${r.id ?? i}`} rec={r} />)}
              </div>
            )}
          </section>

          {/* ══ Danger zone ══ */}
          <section className="rounded-2xl border border-red-100 bg-red-50/30 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-red-600">Delete this course</p>
                <p className="text-xs text-stone-500 mt-1 max-w-md">
                  Permanently removes the course with its modules, lessons and scheduled classes — for every
                  enrolled student. This cannot be undone.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setDeleteError(null); setConfirmDelete(true) }}
                className="shrink-0 border border-red-200 text-red-600 font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:bg-red-100/60 transition-all w-max"
              >
                Delete Course
              </button>
            </div>
          </section>
        </>
      )}

      {showEdit && course && <CreateCourseModal course={course} onClose={() => setShowEdit(false)} />}
      {topicModal.open && (
        <CreateTopicModal
          courseId={courseId}
          topic={topicModal.topic}
          onClose={() => setTopicModal({ open: false, topic: null })}
        />
      )}
      {lessonModal.open && (
        <CreateLessonModal
          course={{ id: Number(courseId), title }}
          topic={lessonModal.topic}
          onClose={() => setLessonModal({ open: false, topic: null })}
        />
      )}
      {showClass && (
        <CreateLiveClassModal
          course={{ id: Number(courseId), title }}
          onClose={() => setShowClass(false)}
        />
      )}
      {classToReschedule && (
        <RescheduleLiveClassModal c={classToReschedule} onClose={() => setClassToReschedule(null)} />
      )}
      {confirmDelete && (
        <ConfirmDeleteModal
          heading="Delete this course?"
          message={<>
            &ldquo;{title}&rdquo; will be permanently removed along with its modules, lessons and scheduled
            classes — for every enrolled student. This cannot be undone.
          </>}
          keepLabel="Keep Course"
          confirmLabel="Yes, Delete Course"
          busy={deleteMutation.isPending}
          error={deleteError}
          onConfirm={() => deleteMutation.mutate()}
          onClose={() => setConfirmDelete(false)}
        />
      )}
      {topicToDelete && (
        <ConfirmDeleteModal
          heading="Delete this module?"
          message={<>
            &ldquo;{topicToDelete.title || 'This module'}&rdquo; will be permanently removed along with
            {topicToDelete.lessons?.length
              ? <> its {topicToDelete.lessons.length} lesson{topicToDelete.lessons.length !== 1 ? 's' : ''}</>
              : ' any lessons inside it'} — for every enrolled student. This cannot be undone.
          </>}
          keepLabel="Keep Module"
          confirmLabel="Yes, Delete Module"
          busy={topicDeleteMutation.isPending}
          error={topicDeleteError}
          onConfirm={() => topicDeleteMutation.mutate(topicToDelete.id)}
          onClose={() => setTopicToDelete(null)}
        />
      )}
      {classToDelete && (
        <ConfirmDeleteClassModal
          c={classToDelete}
          busy={deleteClassMutation.isPending}
          error={classDeleteError}
          onConfirm={confirmDeleteClass}
          onClose={() => setClassToDelete(null)}
        />
      )}
    </div>
  )
}
